import { useMemo } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { StockProjectionExcessGraphProps } from './StockBuild.types'
import { format } from 'date-fns'
import { Loader } from 'Common/Loader/Loader'

export const StockProjectionExcessGraph = ({
  isLoading,
  xAxisData,
  excess,
  minimumInventory,
  safetyStockVolume,
  weekStartInventory,
  traceOneName,
  traceTwoName,
  traceThreeName,
  traceFourName,
}: StockProjectionExcessGraphProps) => {
  const hovertemplate = () => {
    return `%{text}<br>` + `<extra></extra>`
  }

  const hoverTextCompiled = useMemo(
    () =>
      xAxisData?.map((dateText: string, index: number) => {
        const textForHover = `${format(
          new Date(dateText),
          'dd MMM yyy'
        )} (Week ${index + 1})`
        return (
          `   <b>Week:  </b>${textForHover}<br>` +
          `   <b>Week start inventory:  </b>${
            weekStartInventory[index] ?? 0
          }<br>` +
          `   <b>Minimum inventory:  </b>${minimumInventory[index] ?? 0}<br>` +
          `   <b>Safety stock:  </b>${safetyStockVolume[index] ?? 0}<br>` +
          `   <b>Excess:  </b>${excess[index] ?? 0}<br>`
        )
      }),
    [xAxisData, weekStartInventory, safetyStockVolume, minimumInventory, excess]
  )

  const data = useMemo(() => {
    const trace1 = {
      x: xAxisData,
      y: excess,
      name: traceOneName,
      type: 'bar',
      textfont: { color: 'transparent' },
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#dedeef',
      },
    }

    const trace2 = {
      x: xAxisData,
      y: minimumInventory,
      type: 'line',
      textfont: { color: 'transparent' },
      name: traceTwoName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#6f6fce',
      },
    }

    const trace3 = {
      x: xAxisData,
      y: safetyStockVolume,
      type: 'line',
      textfont: { color: 'transparent' },
      name: traceThreeName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#00a2e0',
      },
    }

    const trace4 = {
      x: xAxisData,
      y: weekStartInventory,
      type: 'line',
      textfont: { color: 'transparent' },
      name: traceFourName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#d467ac',
      },
    }

    return [trace1, trace2, trace3, trace4]
  }, [
    xAxisData,
    excess,
    minimumInventory,
    safetyStockVolume,
    weekStartInventory,
    traceFourName,
    traceOneName,
    traceThreeName,
    traceTwoName,
    hoverTextCompiled,
  ])

  const layout = {
    height: 280,
    barmode: 'stack',
    xaxis: {
      tickformat: '%d %b %y',
      nticks: 10,
    },
    yaxis: {
      title: {
        text: 'Stock projection excess',
        standoff: 5,
      },
      tickmode: 'array',
      showgrid: true,
    },
    legend: {
      orientation: 'h',
      traceorder: 'normal',
      y: 1.2,
    },
    margin: {
      l: 70,
      r: 30,
      b: 50,
      t: 50,
    },
  }
  if (isLoading) {
    return <Loader />
  }
  return <Graph data={data} layout={layout} />
}
